export interface CustomerType {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string;
	address?: string;
	addressProofUrl?: string;
	state?: string;
	city?: string;
	companyName?: string;
	jobTitle?: string;
	joinedAt?: string;
	employmentType?: string;
	employmentContractUrl?: string;
	linkedInUrl?: string;
	photoUrl?: string;
	idCardType?: string;
	idCardNumber?: string;
	meterNumber?: string;
	electricityDistributionCompany?: string;
	bvn?: string;
	monoVerified: boolean;
	bvnVerifyDetails?: BvnVerifyDetails;
	internationalPassportVerificationDetails?: InternationalPassportVerificationDetails;
	ninVerificationDetails?: NinVerificationDetails;
	addressVerificationDetails?: AddressVerificationDetails;
}

// used in Util admin Page.
export interface Customer {
	id: string;
	name: string;
	phoneNumber: string;
	email: string;
	meterNumber: string;
	accountNumber: string;
	district: string;
	date: string;
}

export interface CreditEngineScore {
	category: CreditEngineScoreType;
	score: number;
	serviceableLoanAmount: number;
	numberOfClosedLoans: number;
	numberOfPerformingLoans: number;
}

export type CreditEngineScoreType =
	| "No Credit History"
	| "No Income Details"
	| "Closed with Performing Loans"
	| "No Disbursed Date Reported"
	| "Very High Risk"
	| "High Risk"
	| "Moderate Risk"
	| "Low Risk";
export interface BvnVerifyDetails {
	id: string;
	firstName: string;
	lastName: string;
	middleName: string;
	dob: string;
	phoneNumber: string;
	phoneNumber2: string;
	email: string;
	gender: string;
	stateOfOrigin: string;
	bvn: string;
	nin: string;
	registrationDate: string;
	lgaOfOrigin: string;
	lgaOfResidence: string;
	maritialStatus: string;
	watchListed: boolean;
	photoId: string;
}

export interface CustomerDashboardModel {
	walletBalance: number;
	bankName: string;
	accountNumber: string;
	fullName: string;
	bnplOffer: BPNLOFFER;
	bnplOfferId: string;
	amountFinanced: number;
	totalOutstanding: number;
	amountDue: number;
	paymentDueDate: string;
}

export enum BPNLOFFER {
	NEW_PACKAGE_OFFER = "New package offer",
	NO_ACTIVE_PACKAGE = "No active package",
	ACTIVE = "Active",
	TRANSACTION_IN_PROGRESS = "Transaction in progress",
}
export interface InternationalPassportVerificationDetails {
	dob: string;
	documentType: string;
	expiryDate: string;
	firstName: string;
	gender: string;
	issuedAt: string;
	issuedDate: string;
	lastName: string;
	middleName: string;
	mobile: string;
	number: string;
	photo: string;
	referenceId: string;
	signature: string;
}

export interface NinVerificationDetails {
	birthCountry?: string;
	birthDate?: string;
	birthLGA?: string;
	birthState?: string;
	educationalLevel?: string;
	email?: string;
	employmentStatus?: string;
	firstName?: string;
	gender?: string;
	height?: string;
	maritalStatus?: string;
	middleName?: string;
	nin?: string;
	nokAddress1?: string;
	nokAddress2?: string;
	nokFirstName?: string;
	nokLga?: string;
	nokMiddleName?: string;
	nokPostalCode?: string;
	nokState?: string;
	nokSurname?: string;
	nokTown?: string;
	oSpokenLang?: string;
	pFirstName?: string;
	photo?: string;
	pMiddleName?: string;
	profession?: string;
	pSurname?: string;
	religion?: string;
	residenceAddress?: string;
	residenceLga?: string;
	residenceState?: string;
	residenceTown?: string;
	residenceStatus?: string;
	selfOriginLga?: string;
	selfOriginPlace?: string;
	selfOriginState?: string;
	signature?: string;
	spokenLanguage?: string;
	surname?: string;
	telephoneNo?: string;
	title?: string;
	userId?: string;
	vNin?: string;
	centralId?: string;
	trackingId?: string;
}

export interface AddressVerificationDetails {
	verified: boolean;
	houseAddress: string;
	houseOwner: string;
	confidenceLevel: number;
	discoCode: string;
}

export interface ProviderCustomerOrder {
	id: string;
	customerName: string;
	customerPhoneNumber: string;
	productName: string;
	paymentDate?: Date;
	status: OrderStatus;
	statusChanged: string;
	customerAddress: string;
	amountPaidFor: number;
	productDescription: string;
	roofType?: string;
	roofTypeImage?: string;
	buildingType: string;
	numberOfFloors?: number;
}

export enum CustomerSearchByFilter {
	CustomerName = 0,
	MeterNumberOrAccountNumber = 1,
	PhoneNumber = 2,
	Username = 3,
}

export interface CustomerQuery {
	page?: number;
	pageSize?: number;
	search?: string;
	searchBy?: CustomerSearchByFilter;
	userId?: string;
	From?: string;
	To?: string;
}

export interface CustomerMeterModel {
	id: string;
	name: string;
	meterNumber?: string | null;
	accountNumber?: string | null;
}

export interface AdminCustomerModel {
	id: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	dateRegistered: string;
	username: string;
	customerType: CustomerTypeEnum;

	userId: string;
	meterNumber: string;
	accountNumber: string;
	utilityId: string;
	lastLogin: string;
	dateJoined: string;

	lastBillingDate: string;
	lastBillingAmount: string;
	lastBillStatus: string;
	isBlacklisted: boolean;

	// Customer-state indicators (utility-admin customer details). All optional:
	// a null/absent value means the source was unavailable / not provisioned and
	// the UI renders a neutral "N/A" / "Unavailable" state.
	walletAccountNumber?: string | null;
	walletBalance?: number | null;
	isNinVerified?: boolean | null;
	/** "Completed" | "Partially submitted" | null */
	energyProfileStatus?: string | null;
	/** true = opted in (Active), false = opted out (Inactive), null = unavailable */
	preferRemoteTokenLoad?: boolean | null;

	// Meters linked to the customer's energy account. Empty/absent when no account / vending unavailable.
	meters?: CustomerMeterModel[];
}

export interface AdmimCustomerDetailsModel {
	firstName: string;
	lastName: string;
	dateOfBirth: string;
	email: string;
	phoneNumber: string;
	accountNumber: string;
	amountPaid: number;
	outstandingAmount: number;
	upcomingPayment: number;
	loanStatus: string;
}

export type CustomerVerificationStatus = "Pending" | "Success" | "Failed" | "Cancelled";

export interface CustomerPaystackCard {
	id: string;
	status: CustomerVerificationStatus;
	bin?: string;
	last4?: string;
	expMonth?: string;
	expYear?: string;
	channel?: string;
	cardType?: string;
	bank?: string;
	countryCode?: string;
	brand?: string;
	reusable?: boolean;
	signature?: string;
	accountName?: string;
}

export interface CustomerPaymentOptions {
	virtualWalletConnected: boolean;
	paystackConnected: boolean;
}
//////

// TypeScript interfaces for the Solar Order JSON structure

// Enums
export enum CustomerTypeEnum {
	Unknown = 0,
	Individual = 1,
	Corporate = 2,
}

export enum OrderType {
	Unknown = 0,
	OneTime = 1,
	Installment = 2,
	PayAsYouGo = 3,
}
export enum OrderStatus {
	Unknown = 0,
	Pending = 1,
	DeliveryInProgress = 2,
	InstallationInProgress = 3,
	CustomerVerificationInProgress = 4,
	Final = 5,
	Failed = 6,
	WaitingCreditApproval = 7,
	FailedCreditApproval = 8,
	DeclinedByCustomer = 9,
	CreditApproved = 10,
	DepositNotPaid = 11,
	OutOfStock = 12,
}

enum EventType {
	CREATED = 1,
	PROCESSED = 2,
	COMPLETED = 8,
	// Additional event types can be added here
}

// Workflow Details
interface WorkflowState {
	correlationId: string;
	orderId: string;
	userId: string;
	companyId: string;
	productId: string;
	orderType: OrderType;
}

export interface WorkflowEvent {
	id: number;
	workflowId: string;
	timestamp: string;
	typeOfEvent: number;
	executedBy: string | null;
	type: number;
}

interface WorkflowDetails {
	state: WorkflowState;
	events: WorkflowEvent[];
}

// Product Details
interface ProductImage {
	id: number;
	imageUrl: string;
}

interface ProductCategory {
	id: number;
	name: string;
}

interface ProductDetails {
	id: string;
	productName: string;
	productCapacity: number;
	price: number;
	activeDuration: number;
	packageDescription: string;
	batteryType: string;
	inverterType: string;
	warranty: string;
	batteryVoltage: string;
	installationCostForBungalow: number;
	installationCostForFloorForStoryBuilding: number;
	images: ProductImage[];
	categories: ProductCategory[];
}

// Cost Overview
interface CostOverview {
	productPrice: BaseAmountModel;
	installmentPrice: number;
	insurancePrice: number;
	tax: number;
	shipping: number;
	totalAmount: BaseAmountModel;
}

// Contact Information
export interface ContactInformation {
	fullName: string;
	email: string;
	phoneNumber: string;
}

// Delivery Information
export interface DeliveryInformation {
	id: string;
	isDeleted: boolean;
	address: string;
	additionalInformation: string | null;
	roofType: RoofType;
	numberOfFloors: number | null;
}

export interface RoofType {
	id: number;
	name: string;
	premiseType: number;
	iconUrl: string;
}

////
export interface MonthlyPaymentDetails {
	id: string;
	depositPaidAt: string;
	durationOfPaymentsInMonths: number;
	deposit: Deposit;
	monthlyPayment: BaseAmountModel;
	interest: Interest;
	interestRate: number;
}

export interface BaseAmountModel {
	currency: string;
	amount: number;
}

export interface CustomersOverview {
	customersRegistered: number;
	customerLogins: number;
	totalNumberOfCustomers: number;
}

export interface Currency extends BaseAmountModel {}

export interface Deposit extends BaseAmountModel {}

export interface Interest extends BaseAmountModel {}

export interface PayAsYouGoDetails {
	id: string;
	tariffPlan: number;
	tariffAmount: Currency;
}

interface StatusDetails {
	status: number;
	updatedAt: string; // ISO date string
}

// Order Details
export interface OrderDetails {
	id: string;
	solarProviderCompanyId: string;
	customerId: string;
	productDetails: ProductDetails;
	solarProductId: string;
	orderStatus: OrderStatus;
	createdAt: string;
	costOverview: CostOverview;
	contactInformation: ContactInformation;
	deliveryInformation: DeliveryInformation;
	monthlyPaymentDetails: MonthlyPaymentDetails;
	issues: any[]; // Could be defined more specifically if structure is known
	noIssues: any | null; // Could be defined more specifically if structure is known

	statusDetails: StatusDetails;
	payAsYouGoDetails: PayAsYouGoDetails;
	orderType: OrderType | string;
}

// Main Solar Order interface
export interface SolarOrder {
	workflowDetails: WorkflowDetails;
	details: OrderDetails;
}
